// WhatsApp sekmesi — bağlı değilken: uyarılar, hazırlık listesi, beyan ve "WhatsApp'ı Bağla".

import React, { useState } from "react";
import { Alert, Box, Link, Typography } from "@mui/material";
import {
  InfoOutlined as InfoIcon,
  ChecklistRounded as ChecklistIcon,
  WhatsApp as WhatsAppIcon,
  GavelRounded as GavelIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { ActionButton, FormCheckbox, SegmentedControl, formatPhoneDisplay } from "../../../components/atoms";
import { SectionCard } from "../../../components/molecules";
import { COLORS, TYPO, CARD_PAD, CARD_GAP, dashboardSectionSx } from "../../../styles/uiConstants";
import { formatDateTime } from "../../../constants/whatsapp";
import DeclarationDialog from "./DeclarationDialog";

const COMMON_NOTES = [
  "Mesaj ücretlerini Meta doğrudan kliniğinize faturalar. Telefondan yazdıklarınız ücretsizdir.",
  "Mesaj şablonları Meta onayından geçer; onaylanana kadar otomatik mesaj gitmez.",
  "Yalnızca onay vermiş hastalara otomatik mesaj gönderilir.",
  "Mesajlarda tanı, ilaç gibi sağlık bilgisi yazılmaz.",
];

// Bağlantı yöntemine göre "bağlanmadan önce bilin" maddeleri
const KNOW_BEFORE = {
  coexistence: [
    "Numaranız telefondaki WhatsApp Business uygulamasında kalır; telefondan yazışmaya ve aramaya devam edersiniz.",
    "Telefondan yazdıklarınız da Mesajlar sayfasına düşer; panel telefonunuzun aynası olur.",
    "Bağlanırken telefonda geçmiş paylaşımını onaylarsanız son 6 aya kadarki sohbetleriniz aktarılır. Hastanıza eşleşen numaralar hastanın konuşmasına, diğerleri \"Yeni kişiler\"e düşer.",
    "Bağlamadan önce WhatsApp Business uygulamasını güncelleyin ve sohbet yedeği alın.",
    "Uygulamayı en az 14 günde bir telefonda açın; açılmazsa bağlantı kopar.",
    "Bilgisayarda WhatsApp Web'i tarayıcıdan kullanın; bağlantıdan sonra yeniden bağlamanız gerekebilir.",
    "Kaybolan mesajlar, tek seferlik görüntüleme ve canlı konum bu numarada kapanır.",
    ...COMMON_NOTES,
  ],
  new: [
    "Numara WhatsApp uygulamasından çıkar; yalnızca bu panelden kullanılır. Eski sohbetler taşınmaz.",
    "Numara şu an WhatsApp veya WhatsApp Business uygulamasında kayıtlıysa, bağlamadan önce uygulamadan hesabı silin (Ayarlar → Hesap → Hesabı sil).",
    ...COMMON_NOTES,
  ],
};

const MODE_OPTIONS = [
  { value: "coexistence", label: "Telefondaki numaram" },
  { value: "new", label: "Yalnız panel için numara" },
];

const PREPARE = {
  coexistence: [
    "Kliniği yönetecek kişinin Facebook hesabı",
    "WhatsApp Business uygulamasının kurulu olduğu telefon (bağlantıyı telefondan onaylarsınız)",
    "Şirket bilgileri (unvan, adres, web sitesi)",
    "Meta'ya tanımlanacak bir ödeme kartı",
  ],
  new: [
    "Kliniği yönetecek kişinin Facebook hesabı",
    "Şirket bilgileri (unvan, adres, web sitesi)",
    "SMS veya arama alabilen bir telefon numarası",
    "Meta'ya tanımlanacak bir ödeme kartı",
  ],
};

function BulletList({ items }) {
  return (
    <Box component="ul" sx={{ m: 0, px: CARD_PAD, py: 1.5, pl: 4.5, display: "flex", flexDirection: "column", gap: 1 }}>
      {items.map((t) => (
        <Typography key={t} component="li" sx={{ fontSize: TYPO.body, color: COLORS.slate700, lineHeight: 1.55 }}>
          {t}
        </Typography>
      ))}
    </Box>
  );
}

const cardSx = { ...dashboardSectionSx, display: "flex", flexDirection: "column" };

/**
 * @param {object} declaration - { version, text, accepted }
 * @param {object|null} account - AccountView (declaration bilgisi için)
 * @param {boolean} accepted - güncel sürüm kabul edildi mi
 * @param {() => Promise<void>} onAccept
 * @param {boolean} accepting
 * @param {() => void} onConnect
 * @param {boolean} connecting
 * @param {string} [progressText] - bağlanırken adım metni
 * @param {() => void} [onCancel] - Meta penceresi beklenirken verilir; "Vazgeç" gösterilir
 * @param {string} [info] - yumuşak bilgi (ör. kullanıcı vazgeçti)
 * @param {string} [error] - sunucu hata metni
 * @param {boolean} [reconnect] - bağlantı koptuktan sonra yeniden bağlama
 * @param {string|null} clinicPhone - Klinik Bilgileri › İletişim ve Adres'teki telefon; yoksa bağlanamaz
 * @param {"coexistence"|"new"} mode - bağlantı yöntemi
 * @param {(mode: string) => void} onModeChange
 * @param {object} palette
 */
export default function ConnectPanel({
  declaration,
  account,
  accepted,
  onAccept,
  accepting,
  onConnect,
  connecting,
  progressText,
  onCancel,
  info,
  error,
  reconnect = false,
  clinicPhone = null,
  mode = "coexistence",
  onModeChange,
  palette,
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const acc = account?.declaration;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: CARD_GAP }}>
      <SectionCard
        title={reconnect ? "WhatsApp'ı yeniden bağlayın" : "WhatsApp Business hattınızı bağlayın"}
        subtitle="Randevu mesajları ve hasta yazışmaları kliniğinizin kendi numarasından gider"
        icon={<WhatsAppIcon sx={{ fontSize: 18 }} />}
        sx={cardSx}
      >
        <Box sx={{ px: CARD_PAD, py: 1.75 }}>
          <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700, lineHeight: 1.6 }}>
            Bağladığınızda randevu oluşturma, değişiklik ve iptal mesajları ile randevudan 24 saat önceki hatırlatma
            hastalarınıza WhatsApp'tan da gider. Hastalarınızın WhatsApp'tan yazdıkları Mesajlar sayfasına düşer ve
            panelden yanıtlanır. Bağlantı Meta'nın güvenli penceresinde, kliniğinizin kendi Meta hesabıyla yapılır.
          </Typography>
          <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500, mt: 1.75, mb: 0.5 }}>
            Klinik telefonu
          </Typography>
          {clinicPhone ? (
            <>
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate800, fontVariantNumeric: "tabular-nums" }}>
                {formatPhoneDisplay(clinicPhone)}
              </Typography>
              <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500, mt: 0.25 }}>
                Meta penceresinde bu numarayı seçin; farklı bir numara bağlanamaz.
              </Typography>
            </>
          ) : (
            <Alert severity="warning" sx={{ borderRadius: 2, fontSize: TYPO.label }}>
              Klinik bilgilerinde telefon numarası yok. WhatsApp bağlamak için önce Klinik Bilgileri › İletişim ve
              Adres bölümüne telefon girilmelidir; sistem yöneticinize başvurun.
            </Alert>
          )}
          <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500, mt: 1.75, mb: 0.75 }}>
            Hangi numarayı bağlayacaksınız?
          </Typography>
          <SegmentedControl
            value={mode}
            onChange={(v) => onModeChange?.(v)}
            options={MODE_OPTIONS}
            ariaLabel="Bağlantı yöntemi"
            palette={palette}
          />
          <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500, mt: 0.75 }}>
            {mode === "coexistence"
              ? "Şu an telefonda kullandığınız WhatsApp Business numarası. Telefonda ve panelde birlikte çalışır."
              : "WhatsApp'ta kullanılmayan ya da uygulamadan silinecek bir numara. Yalnızca panelden kullanılır."}
          </Typography>
        </Box>
      </SectionCard>

      <Box sx={{ display: "grid", gap: CARD_GAP, gridTemplateColumns: { xs: "1fr", lg: "1.4fr 1fr" } }}>
        <SectionCard
          title="Bağlanmadan önce bilin"
          subtitle="Numaranız ve sorumluluklarınız"
          icon={<InfoIcon sx={{ fontSize: 18 }} />}
          sx={cardSx}
        >
          <BulletList items={KNOW_BEFORE[mode] || KNOW_BEFORE.coexistence} />
        </SectionCard>
        <SectionCard
          title="Hazırlayın"
          subtitle="Meta penceresinde istenecekler"
          icon={<ChecklistIcon sx={{ fontSize: 18 }} />}
          sx={cardSx}
        >
          <BulletList items={PREPARE[mode] || PREPARE.coexistence} />
          <Box sx={{ px: CARD_PAD, pb: 2 }}>
            <Alert severity="info" sx={{ borderRadius: 2, fontSize: TYPO.label }}>
              Hasta sayınız fazlaysa Meta'da işletmenizi doğrulatın; doğrulanmamış hesapların günlük mesaj başlatma
              limiti düşüktür.
            </Alert>
          </Box>
        </SectionCard>
      </Box>

      <SectionCard
        title="Beyan ve bağlantı"
        subtitle="Bağlamadan önce beyanı okuyup kabul etmeniz gerekir"
        icon={<GavelIcon sx={{ fontSize: 18 }} />}
        sx={cardSx}
      >
        <Box sx={{ px: CARD_PAD, py: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1 }}>
            <FormCheckbox
              palette={palette}
              checked={accepted}
              disabled={accepted || !declaration?.version}
              loading={accepting}
              onChange={(v) => {
                if (v && !accepted) onAccept();
              }}
              label={
                <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700 }}>
                  <Link
                    component="button"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDialogOpen(true);
                    }}
                    sx={{ color: palette.primary, fontSize: "inherit", verticalAlign: "baseline" }}
                  >
                    WhatsApp Kullanım ve Sorumluluk Beyanı
                  </Link>
                  'nı okudum, kabul ediyorum.
                </Typography>
              }
            />
          </Box>
          {accepted && acc?.acceptedAt ? (
            <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500, mt: -1 }}>
              Kabul edildi: {[acc.acceptedByName, formatDateTime(acc.acceptedAt)].filter(Boolean).join(" · ")}
            </Typography>
          ) : null}

          {error ? (
            <Alert severity="error" sx={{ borderRadius: 2, fontSize: TYPO.label }}>
              {error}
            </Alert>
          ) : null}
          {info && !error ? (
            <Alert severity="info" sx={{ borderRadius: 2, fontSize: TYPO.label }}>
              {info}
            </Alert>
          ) : null}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <ActionButton
              variant="confirm"
              palette={palette}
              size="medium"
              icon={<WhatsAppIcon sx={{ fontSize: 16 }} />}
              label={reconnect ? "Yeniden Bağla" : "WhatsApp'ı Bağla"}
              loading={connecting}
              disabled={!accepted || !clinicPhone || connecting}
              onClick={onConnect}
              sx={{ px: 2.5 }}
            />
            {connecting && progressText ? (
              <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>{progressText}</Typography>
            ) : null}
            {connecting && onCancel ? (
              <ActionButton variant="edit" tone="outline" label="Vazgeç" icon={<CloseIcon sx={{ fontSize: 15 }} />} onClick={onCancel} />
            ) : null}
          </Box>
        </Box>
      </SectionCard>

      <DeclarationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        text={declaration?.text}
        version={declaration?.version}
      />
    </Box>
  );
}
